import React from 'react';
import { Card, CardContent } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { FileText, Droplet, Waves, Trash2, Zap, Flame } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { useTranslation } from "react-i18next";

const SERVICE_CONFIG: Record<string, { icon: any, theme: string }> = {
  water: { icon: Droplet, theme: "blue" },
  sewer: { icon: Waves, theme: "teal" },
  trash: { icon: Trash2, theme: "green" },
  electricity: { icon: Zap, theme: "purple" },
  gas: { icon: Flame, theme: "orange" },
};
const FALLBACK_CONFIG = { icon: FileText, theme: "blue" };

export function PlanDetails({ planConfig }: { planConfig?: any }) {
  const { t } = useTranslation();
  const coveredServices = React.useMemo(() => {
    const rates = planConfig?.result?.rates;
    if (!rates || typeof rates !== 'object') return [];
    
    return Object.keys(rates).map(key => {
      const name = key ? String(key).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : t("Unknown");
      return { id: key, name };
    });
  }, [planConfig, t]);

  const planName = planConfig?.result?.planName || planConfig?.result?.name || planConfig?.result?.plan_name || t("Active Plan Details");
  const planCode = planConfig?.result?.planCode || planConfig?.result?.code || planConfig?.result?.plan_code || t("PLN-CONFIG");
  return (
    <Card className="shadow-sm border-gray-200 mb-8 overflow-hidden rounded-xl">
      <CardContent className="p-0">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center shrink-0 border border-blue-100">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-lg font-bold text-gray-900">{planName}</h2>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-0 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {t("Standard")}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 font-medium">{planCode}</p>
              </div>
            </div>
            <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-100 border-0 font-bold px-3 py-1 rounded-full text-[11px] tracking-wide">
              {t("Active")}
            </Badge>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/30">
          <p className="text-[11px] font-bold text-gray-500 mb-3">{t("Covered Services")}</p>
          <div className="flex flex-wrap gap-3">
            {coveredServices.map(s => {
              const configId = s?.id ? String(s.id).toLowerCase() : null;
              const configName = s?.name ? String(s.name).toLowerCase() : null;
              const config = (configId ? SERVICE_CONFIG[configId] : undefined) || (configName ? SERVICE_CONFIG[configName] : undefined) || FALLBACK_CONFIG;
              const Icon = s?.icon || config.icon;
              const currentTheme = s?.theme || config.theme;
              
              const themeColors: Record<string, string> = {
                blue: "bg-blue-50 text-blue-600 border-blue-200",
                teal: "bg-teal-50 text-teal-600 border-teal-200",
                green: "bg-green-50 text-green-600 border-green-200",
                purple: "bg-purple-50 text-purple-600 border-purple-200",
                orange: "bg-orange-50 text-orange-600 border-orange-200",
              };
              return (
                <Badge key={s.id} variant="outline" className={cn("gap-1.5 px-3 py-1 text-sm font-medium rounded-full", themeColors[currentTheme] || themeColors.blue)}>
                  <Icon className="w-4 h-4" /> {s.name}
                </Badge>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
